import { useMemo } from "react";
import { useV2ListGovernancesForAccountQuery } from "@generated";
import type { GovernanceRow } from "../types/Governance";

export interface UseGovernancesListResult {
  rows: GovernanceRow[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
}

export interface UseGovernancesListArgs {
  archived: boolean;
}

export const useGovernancesList = ({
  archived,
}: UseGovernancesListArgs): UseGovernancesListResult => {
  const { data, isLoading, isFetching, refetch } =
    useV2ListGovernancesForAccountQuery({
      filter: { active: !archived },
      include: ["resource", "phase"],
    });

  // Memoize the rows array so its reference stays stable across renders
  // when the underlying RTK Query data hasn't changed. Without this, every
  // re-render (e.g. while a move() mutation is in flight) produces a new
  // array reference, which invalidates AdminListTable's sortedData memo
  // and triggers its data->localRows sync effect — clobbering any
  // optimistic drag-drop update.
  const rows: GovernanceRow[] = useMemo(
    () =>
      data?.data
        ?.map((record: any) => ({
          id: String(record.id),
          position: record.attributes.position ?? 0,
          name: record.attributes.name ?? "",
          resourceId: record.relationships?.resource?.data?.id ?? null,
          resourceName: record.attributes["resource-name"] ?? null,
          phaseId: record.relationships?.phase?.data?.id ?? null,
          phaseName: record.attributes["phase-name"] ?? null,
          calculationType: record.attributes["calculation-type"] ?? null,
          fixedHours: record.attributes["fixed-hours"] ?? null,
          rate: record.attributes.rate ?? null,
          filterType: record.attributes["filter-type"] ?? null,
          filterId: record.attributes["filter-id"] ?? null,
          allocationMethod: record.attributes["allocation-method"] ?? null,
          assignEffortToService:
            !!record.attributes["assign-effort-to-service"],
          required: !!record.attributes.required,
          deletedAt: record.attributes["deleted-at"] ?? null,
        }))
        // List governances in position order regardless of the order the
        // API returns them in.
        .sort((a, b) => a.position - b.position) ?? [],
    [data]
  );

  return {
    rows,
    totalCount: data?.meta?.["record-count"] ?? rows.length,
    isLoading,
    isFetching,
    refetch,
  };
};
